import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, User } from 'lucide-react';
import { formatRelativeReviewTime } from '../../utils/relativeTimeAr';
import { useStoreInfo } from '../../store/useStoreInfo';
import * as S from './styles';

export type PublishedReview = {
  id: number;
  created_at: string;
  customer_label: string;
  rating: number;
  comment: string;
  product_name: string;
};

type ReviewAggregate = {
  review_count: number;
  rating_value: number;
};

type RatingDistribution = {
  '1': number;
  '2': number;
  '3': number;
  '4': number;
  '5': number;
};

type ApiResponse = {
  success?: boolean;
  data?: {
    reviews: PublishedReview[];
    total?: number;
    aggregate?: ReviewAggregate;
    rating_distribution?: RatingDistribution;
    recommendation_percent?: number;
  };
};

/** بيانات منظّمة لنتائج Google (AggregateRating) — يجب أن تطابق نص الملخص الظاهر */
function buildReviewStructuredData(
  storeName: string,
  productId: number | undefined,
  productName: string | undefined,
  productCanonicalUrl: string,
  storeUrl: string,
  agg: ReviewAggregate,
): Record<string, unknown> {
  const ar = {
    '@type': 'AggregateRating' as const,
    ratingValue: String(agg.rating_value),
    bestRating: '5',
    worstRating: '1',
    reviewCount: String(agg.review_count),
  };
  if (productId != null) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: (productName || 'منتج').trim() || 'منتج',
      url: productCanonicalUrl,
      aggregateRating: ar,
    };
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    name: storeName,
    url: storeUrl,
    aggregateRating: ar,
  };
}

function apiBase(): string {
  return import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com').replace(/\/$/, '');
}

/** أحدث التقييمات أولاً (يتوافق مع الـ API ويثبت الترتيب عند «المزيد»). */
function sortPublishedReviewsNewestFirst(items: PublishedReview[]): PublishedReview[] {
  return [...items].sort((a, b) => {
    const ta = Date.parse(a.created_at);
    const tb = Date.parse(b.created_at);
    if (!Number.isNaN(ta) && !Number.isNaN(tb) && tb !== ta) return tb - ta;
    return b.id - a.id;
  });
}

function RatingBadge({ value, compact }: { value: number; compact?: boolean }) {
  const n = Math.min(5, Math.max(0, Math.round(value)));
  const iconSize = compact ? 12 : 13;
  const display = String(n);

  return (
    <S.RatingBadge
      $compact={compact}
      aria-label={`تقييم ${n} من 5 نجوم`}
    >
      <S.RatingBadgeInner $compact={compact}>
        <Star
          size={iconSize}
          strokeWidth={1.65}
          fill="oklch(0.65 0.24 33.09)"
          color="oklch(0.65 0.24 33.09)"
          aria-hidden
        />
        <S.RatingBadgeValue $compact={compact}>{display}</S.RatingBadgeValue>
      </S.RatingBadgeInner>
    </S.RatingBadge>
  );
}

/** أرقام لاتينية 0123456789 (لا أرقام هندية شرقية) */
function formatLatinNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat('en-US', options).format(value);
}

/** نجوم بقص بصري (5 نجوم) — اتجاه ltr */
function AggregateStars({
  value,
  embedded,
  summary,
}: {
  value: number;
  embedded: boolean;
  /** نجوم الملخّص العلوي — لون هوية المشروع */
  summary?: boolean;
}) {
  const s = summary ? (embedded ? 16 : 19) : embedded ? 15 : 18;
  const v = Math.min(5, Math.max(0, value));
  return (
    <S.AggregateStarRow $embedded={embedded} aria-hidden $summary={!!summary}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fillRatio = Math.min(1, Math.max(0, v - i));
        return (
          <S.StarSlot key={i} $size={s}>
            <S.StarBack>
              <Star
                size={s}
                strokeWidth={0}
                fill="currentColor"
                color="currentColor"
                aria-hidden
              />
            </S.StarBack>
            {fillRatio > 0 ? (
              <S.StarFillClip style={{ width: `${fillRatio * 100}%` }} aria-hidden>
                <S.StarFore $size={s} $summary={!!summary}>
                  <Star
                    size={s}
                    strokeWidth={0}
                    fill="currentColor"
                    color="currentColor"
                    aria-hidden
                  />
                </S.StarFore>
              </S.StarFillClip>
            ) : null}
          </S.StarSlot>
        );
      })}
    </S.AggregateStarRow>
  );
}

type CustomerReviewsSectionProps = {
  /** عند التعيين تُجلب تقييمات تتضمن هذا المنتج فقط */
  productId?: number;
  /** اسم المنتج (للـ JSON-LD عند التقييم داخل النافذة) */
  productName?: string;
  /** صفحة رئيسية كاملة العرض أو داخل نافذة المنتج */
  variant?: 'page' | 'embedded';
};

export function CustomerReviewsSection({
  productId,
  productName,
  variant = 'page',
}: CustomerReviewsSectionProps) {
  const [reviews, setReviews] = useState<PublishedReview[]>([]);
  const [aggregate, setAggregate] = useState<ReviewAggregate | null>(null);
  const [ratingDistribution, setRatingDistribution] = useState<RatingDistribution | null>(
    null,
  );
  const [recommendationPercent, setRecommendationPercent] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const storeName = useStoreInfo(s => s.data?.name) || 'المتجر';

  const embedded = variant === 'embedded';
  const limit = 5;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (productId != null) qs.set('product_id', String(productId));
        const res = await fetch(`${apiBase()}/api/reviews/published?${qs}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const json = (await res.json()) as ApiResponse;
        if (!cancelled && page === 1) {
          setAggregate(json.data?.aggregate ?? null);
          setRatingDistribution(json.data?.rating_distribution ?? null);
          setRecommendationPercent(
            json.data?.recommendation_percent ?? null,
          );
        }
        if (!cancelled && json?.success && json.data?.reviews) {
          const newReviews = json.data.reviews;
          setReviews(prev =>
            sortPublishedReviewsNewestFirst(
              page === 1 ? newReviews : [...prev, ...newReviews],
            ),
          );
          
          const total = json.data.total || 0;
          setHasMore(page * limit < total);
        } else if (!cancelled && page === 1) {
          setReviews([]);
          setAggregate(null);
          setRatingDistribution(null);
          setRecommendationPercent(null);
          setHasMore(false);
        }
      } catch {
        if (!cancelled && page === 1) {
          setReviews([]);
          setAggregate(null);
          setRatingDistribution(null);
          setRecommendationPercent(null);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId, page]);

  const origin =
    typeof window !== 'undefined' ? window.location.origin : '';
  const storeCanonicalUrl = origin ? `${origin}/` : '';
  const productCanonicalUrl =
    origin && productId != null ? `${origin}/product/${productId}` : storeCanonicalUrl;

  const structuredDataJson = useMemo(() => {
    if (!aggregate || aggregate.review_count < 1) return '';
    const obj = buildReviewStructuredData(
      storeName,
      productId,
      productName,
      productCanonicalUrl,
      storeCanonicalUrl,
      aggregate,
    );
    return JSON.stringify(obj);
  }, [
    aggregate,
    storeName,
    productId,
    productName,
    productCanonicalUrl,
    storeCanonicalUrl,
  ]);

  const ldId =
    productId != null
      ? `product-reviews-jsonld-${productId}`
      : 'store-reviews-jsonld';

  const centerHeader = true;

  if (loading) {
    return (
      <S.Section $embedded={embedded}>
        <S.Header $centerHeader={centerHeader}>
          <S.SkeletonLine $w="140px" $h="14px" style={{ marginBottom: 8 }} />
          <S.SkeletonLine $w="220px" $h="11px" />
        </S.Header>
        <S.List>
          {[0, 1].map((k) => (
            <S.SkeletonCard key={k}>
              <S.SkeletonMetaRow>
                <S.SkeletonAvatar $compact={embedded} />
                <S.SkeletonMetaCol>
                  <S.SkeletonLine $w="45%" $h="12px" />
                  <S.SkeletonLine $w="35%" $h="10px" />
                </S.SkeletonMetaCol>
              </S.SkeletonMetaRow>
              <S.SkeletonLine $w="100%" $h="10px" style={{ marginBottom: 6 }} />
              <S.SkeletonLine $w="85%" $h="10px" />
            </S.SkeletonCard>
          ))}
        </S.List>
      </S.Section>
    );
  }

  if (reviews.length === 0) return null;

  const title = 'آراء العملاء';
  const subtitle =
    productId != null
      ? 'من عملاء جرّبوا هذا المنتج في طلباتهم'
      : 'تقييمات من عملاء بعد تجربة الطلب والتوصيل';

  const countLabel = aggregate
    ? formatLatinNumber(aggregate.review_count)
    : '';
  const hasAggregate =
    aggregate && aggregate.review_count > 0 && countLabel;
  const scoreLabel = aggregate
    ? formatLatinNumber(aggregate.rating_value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })
    : '';
  const summaryAria =
    hasAggregate && aggregate
      ? productId != null
        ? `تقييم المنتج: ${scoreLabel} من 5، ${countLabel} تقييماً`
        : `تقييم المتجر: ${scoreLabel} من 5، ${countLabel} تقييماً`
      : undefined;

  const dist = ratingDistribution;
  const totalRated = aggregate?.review_count ?? 0;
  const countStar = (n: 1 | 2 | 3 | 4 | 5) =>
    dist ? Math.max(0, Number((dist as Record<string, number>)[String(n)] ?? 0)) : 0;
  const pctForStar = (n: 1 | 2 | 3 | 4 | 5) =>
    totalRated < 1 ? 0 : Math.round((100 * countStar(n)) / totalRated);

  const recommendValue =
    recommendationPercent != null
      ? Math.min(100, Math.max(0, recommendationPercent))
      : dist && totalRated > 0
        ? Math.min(
            100,
            Math.round(
              (100 * (countStar(4) + countStar(5))) / totalRated,
            ),
          )
        : 0;
  const recLabel = formatLatinNumber(recommendValue);

  return (
    <S.Section $embedded={embedded} aria-label={title}>
      {structuredDataJson ? (
        <Helmet>
          <script id={ldId} type="application/ld+json">
            {structuredDataJson}
          </script>
        </Helmet>
      ) : null}
      <S.Header $centerHeader={centerHeader}>
        <S.Title $centerHeader={centerHeader}>{title}</S.Title>
        <S.Subtitle $centerHeader={centerHeader}>{subtitle}</S.Subtitle>
        {hasAggregate && aggregate ? (
          <S.ReviewsDashboard
            $embedded={embedded}
            role="status"
            aria-label={summaryAria}
          >
            <S.DashboardSummary $embedded={embedded}>
              <AggregateStars
                value={aggregate.rating_value}
                embedded={embedded}
                summary
              />
              <S.SummaryScore $embedded={embedded}>{scoreLabel}</S.SummaryScore>
              <S.SummaryBasedOn $embedded={embedded}>
                بناءً على {countLabel} تقييم
              </S.SummaryBasedOn>
              <S.RecommendRow $embedded={embedded}>
                <S.RecommendPct
                  $embedded={embedded}
                  dir="ltr"
                >{`%${recLabel}`}</S.RecommendPct>
                <S.RecommendLabel $embedded={embedded}>
                  {productId != null
                    ? 'أوصوا بالمنتج'
                    : 'أوصوا بالمتجر'}
                </S.RecommendLabel>
              </S.RecommendRow>
            </S.DashboardSummary>
            <S.DashboardDist>
              {([5, 4, 3, 2, 1] as const).map((star) => {
                const p = pctForStar(star);
                return (
                  <S.DistRow key={star}>
                    <S.DistStarNum>
                      {formatLatinNumber(star)}
                    </S.DistStarNum>
                    <S.DistStarIcon>
                      <Star
                        size={14}
                        strokeWidth={0}
                        fill="currentColor"
                        color="currentColor"
                        aria-hidden
                      />
                    </S.DistStarIcon>
                    <S.DistTrack>
                      <S.DistFill $pct={p} />
                    </S.DistTrack>
                    <S.DistPct>
                      {formatLatinNumber(p)}%
                    </S.DistPct>
                  </S.DistRow>
                );
              })}
            </S.DashboardDist>
          </S.ReviewsDashboard>
        ) : null}
      </S.Header>
      <S.List>
        {reviews.map((r) => {
          const dateStr = formatRelativeReviewTime(r.created_at);
          const comment = (r.comment || '').trim();

          return (
            <S.Card key={r.id}>
              <S.CardTop>
                <S.MetaRow>
                  <S.AvatarIconWrap $compact={embedded} aria-hidden>
                    <User
                      size={embedded ? 18 : 20}
                      strokeWidth={1.65}
                      aria-hidden
                    />
                  </S.AvatarIconWrap>
                  <S.Meta>
                    <S.CustomerName>{r.customer_label}</S.CustomerName>
                    {dateStr ? <S.DateText>{dateStr}</S.DateText> : null}
                    {r.product_name && !productId ? (
                      <S.DateText>{r.product_name}</S.DateText>
                    ) : null}
                  </S.Meta>
                </S.MetaRow>
                <RatingBadge value={r.rating} compact={embedded} />
              </S.CardTop>
              {comment ? (
                <S.Comment $embedded={embedded}>{comment}</S.Comment>
              ) : null}
            </S.Card>
          );
        })}
      </S.List>
      {hasMore && (
        <S.LoadMoreButton onClick={() => setPage((p) => p + 1)} disabled={loadingMore}>
          {loadingMore ? 'جاري التحميل...' : 'المزيد'}
        </S.LoadMoreButton>
      )}
    </S.Section>
  );
}

export default CustomerReviewsSection;
